import React, {useEffect, useState} from 'react'
import { Link, useNavigate } from 'react-router-dom';
import './Dashboard.css';
import { FaWhatsapp, FaTelegramPlane, FaMobileAlt, FaEnvelope, FaExclamationTriangle } from 'react-icons/fa';

import { CURRENCY_EURO, urlAccount, urlBalance } from '../../../../util/Constants';
import { conf } from '../../../../config/config';

import Loader from '../../../components/Loader/Loader';
import Card from '../../../components/Card/Card';
import { tempAccountStats, tempTransactions } from '../../../../demo/demo';
import { getAccountBalance, getAccountDashboard } from '../../../../services/accountService';
import { modelProfilePath } from '../../../../services/modelService';
import { getModelPhoto } from '../../../../util/ModelPhotos';

const formatCurrency = (amount, currencySymbol) => `${currencySymbol}${Number(amount || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
})}`

const normalizedStatus = (value) => String(value || '').trim().toLowerCase().replace(/[\s_-]+/g, '')
const activeCampaign = (dashboard) => {
    const campaigns = Array.isArray(dashboard?.campaigns)
        ? dashboard.campaigns
        : dashboard?.activeCampaign || dashboard?.campaign ? [dashboard.activeCampaign || dashboard.campaign] : []
    return campaigns.find((item) => ['active', 'auto'].includes(normalizedStatus(item?.status))) || campaigns[0] || null
}
const campaignModelStatus = (campaign, modelProfile) => campaign?.modelStatus || campaign?.model?.status || modelProfile?.status || ''
const isUnavailableModel = (status) => ['inactive', 'break', 'paused'].includes(normalizedStatus(status))

const Dashboard = ({ loading }) => {
    const navigate = useNavigate()
    const [dashboard, setDashboard] = useState(null)
    const [canonicalBalance, setCanonicalBalance] = useState(null)
    const [profileLoading, setProfileLoading] = useState(true)
    const [balanceLoading, setBalanceLoading] = useState(true)
    const [profileError, setProfileError] = useState('')
    const currency = conf.currency || CURRENCY_EURO
    const currencySymbol = currency === CURRENCY_EURO ? '€' : '$'

    useEffect(() => {
        let mounted = true

        getAccountDashboard()
            .then((accountDashboard) => {
                if (mounted) setDashboard(accountDashboard || {})
            })
            .catch((error) => {
                if (mounted) setProfileError(error.message || 'We could not load your member dashboard.')
            })
            .finally(() => {
                if (mounted) setProfileLoading(false)
            })

        getAccountBalance()
            .then((accountBalance) => {
                if (mounted) setCanonicalBalance(accountBalance || {})
            })
            .catch(() => {
                // Keep the normalized dashboard balance as a resilience fallback.
            })
            .finally(() => {
                if (mounted) setBalanceLoading(false)
            })

        return () => {
            mounted = false
        }
    }, [])

    const balance = canonicalBalance || dashboard?.balance || {}
    const modelProfile = dashboard?.model || null
    const campaign = activeCampaign(dashboard)
    const campaignStatus = normalizedStatus(campaign?.status)
    const campaignIsActive = ['active', 'auto'].includes(campaignStatus)
    const modelStatus = campaignModelStatus(campaign, modelProfile)
    const modelUnavailable = campaignIsActive && isUnavailableModel(modelStatus)
    const campaignStatusLabel = campaignStatus === 'auto' ? 'Auto-pay' : campaignIsActive ? 'Active' : campaignStatus === 'inactive' ? 'Inactive' : (modelProfile?.status || 'Inactive')
    const campaignCity = modelProfile?.city || campaign?.model?.city || campaign?.city || ''
    const advertisementPath = modelProfile?.uuid ? `/account/ad/${modelProfile.uuid}` : '/account/ad'
    const transactions = Array.isArray(dashboard?.transactions) ? dashboard.transactions : tempTransactions
    const cardType = balance.cardType || tempAccountStats.cardType
    const availableBalance = balance.availableBalance ?? tempAccountStats.availableBalance
    const premium = modelProfile?.premium
        || modelProfile?.data?.premium
        || balance.premium
        || balance.data?.premium
        || balance.model?.premium
        || null
    const premiumBalanceAfter = Number(premium?.advertisingBalanceAfterPurchase)
    const advertisingBalance = Number.isFinite(premiumBalanceAfter) && premiumBalanceAfter >= 0
        ? premiumBalanceAfter
        : balance.advertisingBalance ?? tempAccountStats.campaignBalance
    const premiumEndTimestamp = premium?.expiresAt ? new Date(premium.expiresAt).getTime() : null
    const premiumIsActive = String(premium?.status || '').toLowerCase() === 'active'
        && (!premium?.expiresAt || (Number.isFinite(premiumEndTimestamp) && premiumEndTimestamp > Date.now()))
    const premiumEndDate = Number.isFinite(premiumEndTimestamp) ? new Date(premiumEndTimestamp).toISOString().slice(0, 10) : null

    const handleGoToBalance = () => {
        navigate(urlAccount + urlBalance)
    }

    return (
        <section className="section-header account-page dashboard-page">
            <div className="view-container">
                {loading ? <Loader className="page-loader" /> : (
                    <>
                        <h1>Account<span>Dashboard</span></h1>

                        <div className="dashboard-layout">
                            <nav className="dashboard-nav" aria-label="Member area">
                                <Link to={'/account'} className="dash-nav-item active" aria-current="page">Dashboard</Link>
                                <Link to={'/account/balance'} className="dash-nav-item">Finances</Link>
                                <Link to={'/account/ad'} className="dash-nav-item">Advertisement</Link>
                                <Link to={'/account/store'} className="dash-nav-item">Store</Link>
                            </nav>

                            <div className="dashboard-content">
                                {profileLoading || balanceLoading ? <Loader className="profile-loader" /> : <Card
                                    link={handleGoToBalance}
                                    cardType={cardType}
                                    currency={currency}
                                    adBalance={advertisingBalance}
                                    availableBalance={availableBalance}
                                    paymentMethods={balance.paymentMethods || null}
                                    premium={premium}
                                    dashboardPlanLink
                                />}

                                <div className="campaigns">
                                    <div className="section-title-row">
                                        <p>Active Advertisement</p>
                                        <Link to={advertisementPath} className="section-btn">Manage</Link>
                                    </div>
                                    {profileLoading && <Loader className="profile-loader" />}
                                    {!profileLoading && profileError && <p className="dashboard-error" role="alert">{profileError}</p>}
                                    {!profileLoading && !profileError && modelProfile && (
                                        <div className="campaign frosted-glow">
                                            <div className="campaign-left">
                                                <img src={getModelPhoto(modelProfile)} alt={`${modelProfile.name || 'Model'} profile`} />
                                            </div>
                                            <div className="campaign-right">
                                                <div className="campaign-name">{modelProfile.name || 'Model profile'}</div>
                                                <div className="campaign-meta">
                                                    <span className="campaign-status">{campaignStatusLabel}</span>
                                                    {campaign?.expiresAt && <span className="campaign-budget">Expires {new Date(campaign.expiresAt).toLocaleDateString()}</span>}
                                                    {campaignCity && <span className="campaign-budget"> in {campaignCity}</span>}
                                                </div>
                                                {modelUnavailable && (
                                                    <div className="campaign-status-warning" role="alert">
                                                        <FaExclamationTriangle aria-hidden="true" />
                                                        <span>Campaign is active, but this model is {normalizedStatus(modelStatus) === 'break' ? 'on break' : 'inactive'}. Her profile is not shown on the website.</span>
                                                    </div>
                                                )}
                                                {modelProfile.description && <div className="campaign-desc-preview">{modelProfile.description}</div>}
                                                <div className="campaign-contacts-preview" aria-label="Available contact methods">
                                                    {modelProfile.contacts?.whatsapp && <FaWhatsapp title="WhatsApp available" aria-label="WhatsApp available" />}
                                                    {modelProfile.contacts?.telegram && <FaTelegramPlane title="Telegram available" aria-label="Telegram available" />}
                                                    {modelProfile.contacts?.phone && <FaMobileAlt className="icon-sm" title="Phone available" aria-label="Phone available" />}
                                                    {modelProfile.contacts?.email && <FaEnvelope className="icon-sm" title="Email available" aria-label="Email available" />}
                                                </div>
                                                <div className="campaign-actions">
                                                    {modelProfile.slug && modelProfile.city && (
                                                        <Link
                                                            to={modelProfilePath(modelProfile.city, modelProfile)}
                                                            className="campaign-action-btn"
                                                        >View Page →</Link>
                                                    )}
                                                    <Link to={advertisementPath} className="campaign-action-btn">Edit Details →</Link>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {!profileLoading && !profileError && !modelProfile && (
                                        <p className="dashboard-empty">No model profile is linked to this account.</p>
                                    )}
                                </div>

                                <div className="transactions">
                                    <div className="section-title-row">
                                        <p>Latest Transactions</p>
                                        <Link to={'/account/balance'} className="section-btn">View All</Link>
                                    </div>
                                    {transactions.length > 0 ? transactions.map((transaction) => (
                                        <div className="transaction" key={transaction.id || transaction.uuid}>
                                            <div className="tx-left">
                                                <div className="tx-desc">{transaction.description}</div>
                                                <div className="tx-date">{transaction.date}</div>
                                            </div>
                                            <div className={'tx-amount' + (transaction.amount >= 0 ? ' positive' : ' negative')}>
                                                {transaction.amount >= 0 ? '+' : '-'}{formatCurrency(Math.abs(transaction.amount), currencySymbol)}
                                            </div>
                                        </div>
                                    )) : <p className="dashboard-empty">No transactions yet.</p>}
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </section>
    )
}

export default Dashboard
